import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { DataState } from '@/components/common/DataState';
import { formatMoney } from '@/lib/money';

// --- Types ---
interface TeamOption {
  id: string;
  name: string;
  slug: string;
  plan: string;
  ownerEmail?: string;
}

interface CatalogTier {
  tier: string;
  displayName: string;
  price: number;
  currency: string;
}
interface CatalogProduct {
  ref: { type: 'platform' | 'plugin' | 'addon'; id: string };
  name: string;
  tiers: CatalogTier[];
}

interface LinkItem {
  productType: 'platform' | 'plugin' | 'addon';
  productId: string;
  tier: string;
}

interface PaymentLink {
  _id: string;
  token: string;
  title: string;
  teamId: string;
  frequency: string;
  currency: string;
  computedAmount: number;
  listAmount?: number;
  discount?: { type: 'percent' | 'amount'; value: number };
  status: string;
  useCount: number;
  usageLimit: number;
  viewCount: number;
  expiresAt: string;
  createdAt: string;
}

interface PricePreview {
  displayAmount: number;
  formattedDisplayAmount: string;
  listAmount: number;
  formattedListAmount: string;
  discountAmount: number;
  discountPercent: number;
  sellingEntity: string;
  currency: string;
  tax: {
    pricing: string;
    taxAmount: number;
    taxRate?: number;
    gatewayComputed: boolean;
    taxName: string;
    entityLegalName: string;
  };
}

/** Smallest-unit → display amount. JPY has no minor unit (÷1, not ÷100). */
function fmtMinor(amount: number, currency: string): string {
  return currency === 'JPY' ? String(amount) : (amount / 100).toFixed(2);
}

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'halfyearly', label: 'Half-yearly' },
  { value: 'yearly', label: 'Yearly' },
];

// Supported billing regions. Currency auto-derives from the country server-side;
// gateway (Razorpay/Stripe) and tax (GST/HST/VAT/…) follow from currency.
const COUNTRIES = [
  { code: 'IN', name: 'India', currency: 'INR' },
  { code: 'CA', name: 'Canada', currency: 'CAD' },
  { code: 'US', name: 'United States', currency: 'USD' },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP' },
  { code: 'AU', name: 'Australia', currency: 'AUD' },
  { code: 'SG', name: 'Singapore', currency: 'SGD' },
  { code: 'AE', name: 'UAE', currency: 'AED' },
  { code: 'DE', name: 'Germany (EU)', currency: 'EUR' },
  { code: 'FR', name: 'France (EU)', currency: 'EUR' },
  { code: 'JP', name: 'Japan', currency: 'JPY' },
];

function payUrl(token: string) {
  return `${window.location.origin}/pay/${token}`;
}

export default function PaymentLinksPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const linksQuery = useQuery({
    queryKey: qk.paymentLinks(),
    queryFn: async () => {
      const res = await api.get('/admin/payment-links');
      return (res.data.data ?? []) as PaymentLink[];
    },
  });

  const teamsQuery = useQuery({
    queryKey: qk.adminTeams(),
    queryFn: async () => {
      const res = await api.get('/admin/billing/teams');
      return (res.data.data ?? []) as TeamOption[];
    },
  });
  const teams = teamsQuery.data ?? [];

  const teamName = (id: string) => teams.find((t) => t.id === id)?.name ?? id.slice(-6);

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/payment-links/${id}`),
    onSuccess: () => {
      toast.success('Link cancelled');
      queryClient.invalidateQueries({ queryKey: qk.paymentLinks() });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not cancel link')),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payment Links</h1>
          <p className="text-gray-500">
            Create a shareable link that lets a specific customer subscribe. Price and tax are
            auto-calculated from the catalog.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          + New link
        </button>
      </div>

      <DataState query={linksQuery}>
        {(links) =>
          links.length === 0 ? (
            <p className="rounded-xl border bg-white p-6 text-sm text-gray-500">
              No payment links yet.
            </p>
          ) : (
            <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                  <tr>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Account</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Uses</th>
                    <th className="px-4 py-3">Expires</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {links.map((l) => (
                    <tr key={l._id}>
                      <td className="px-4 py-3 font-medium text-gray-900">{l.title}</td>
                      <td className="px-4 py-3 text-gray-600">{teamName(l.teamId)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {l.currency} {fmtMinor(l.computedAmount, l.currency)} · {l.frequency}
                        {l.listAmount && l.listAmount > l.computedAmount && (
                          <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                            {Number(
                              (((l.listAmount - l.computedAmount) / l.listAmount) * 100).toFixed(2)
                            )}
                            % off
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={
                            l.status === 'active'
                              ? 'rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700'
                              : 'rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600'
                          }
                        >
                          {l.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {l.useCount}/{l.usageLimit} · {l.viewCount} views
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {new Date(l.expiresAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(payUrl(l.token));
                            toast.success('Link copied');
                          }}
                          className="mr-3 text-blue-600 hover:underline"
                        >
                          Copy
                        </button>
                        {l.status === 'active' && (
                          <button
                            onClick={() => cancelMutation.mutate(l._id)}
                            className="text-red-600 hover:underline"
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </DataState>

      {showCreate && (
        <CreateLinkModal
          teams={teams}
          onClose={() => setShowCreate(false)}
          onCreated={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}

function CreateLinkModal({
  teams,
  onClose,
  onCreated,
}: {
  teams: TeamOption[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const queryClient = useQueryClient();
  const [teamId, setTeamId] = useState('');
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState<string | null>(null);
  const [hasRegion, setHasRegion] = useState(true);
  const [regionCountry, setRegionCountry] = useState('IN');
  const [selected, setSelected] = useState<LinkItem[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [taxMode, setTaxMode] = useState<'auto' | 'inclusive' | 'exclusive'>('auto');
  const [discountType, setDiscountType] = useState<'none' | 'percent' | 'amount'>('none');
  const [discountValue, setDiscountValue] = useState('');
  const [expiryDays, setExpiryDays] = useState(14);
  const [usageLimit, setUsageLimit] = useState(1);
  const [preview, setPreview] = useState<PricePreview | null>(null);

  function loadProducts(id: string) {
    api
      .get(`/admin/payment-links/products?teamId=${id}`)
      .then((r) => {
        const d = r.data.data;
        setProducts(d.products ?? []);
        setCurrency(d.currency ?? '');
        setCountry(d.country ?? null);
        setHasRegion(Boolean(d.hasCountry) && Boolean(d.hasCurrency));
        if (d.country) setRegionCountry(d.country);
      })
      .catch(() => toast.error('Could not load products for this team'));
  }

  // Load the team's purchasable products when a team is chosen.
  useEffect(() => {
    if (!teamId) return;
    setSelected([]);
    setProducts([]);
    loadProducts(teamId);
  }, [teamId]);

  // Currency auto-derives from the account's country. When it's missing, the
  // admin sets the country here and everything downstream follows.
  const regionMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/payment-links/account-region', { teamId, country: regionCountry }),
    onSuccess: () => {
      toast.success('Account region updated');
      loadProducts(teamId);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not set region')),
  });

  // Sent only when set: percent of the price, or a fixed amount off per month
  // (major units). Either way it comes off the pre-tax price, every cycle.
  const discount = useMemo(() => {
    const value = Number(discountValue);
    if (discountType === 'none' || !(value > 0)) return undefined;
    return { type: discountType, value };
  }, [discountType, discountValue]);

  // Live price preview whenever the offer changes.
  const [previewError, setPreviewError] = useState<string | null>(null);
  useEffect(() => {
    if (!teamId || selected.length === 0) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const t = setTimeout(() => {
      api
        .post('/admin/payment-links/preview-price', {
          teamId,
          items: selected,
          frequency,
          taxMode,
          discount,
        })
        .then((r) => {
          setPreview(r.data.data);
          setPreviewError(null);
        })
        .catch((err) => {
          setPreview(null);
          setPreviewError(apiErrorMessage(err, 'Could not price this link'));
        });
    }, 250);
    return () => clearTimeout(t);
  }, [teamId, selected, frequency, taxMode, discount]);

  const key = (p: CatalogProduct) => `${p.ref.type}:${p.ref.id}`;
  const selectedTierFor = (p: CatalogProduct) =>
    selected.find((s) => s.productType === p.ref.type && s.productId === p.ref.id)?.tier ?? '';

  function setTier(p: CatalogProduct, tier: string) {
    setSelected((prev) => {
      const rest = prev.filter((s) => !(s.productType === p.ref.type && s.productId === p.ref.id));
      if (!tier) return rest;
      return [...rest, { productType: p.ref.type, productId: p.ref.id, tier }];
    });
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const expiresAt = new Date(Date.now() + expiryDays * 86400000).toISOString();
      const res = await api.post('/admin/payment-links', {
        teamId,
        title: title.trim(),
        description: description.trim() || undefined,
        items: selected,
        frequency,
        taxMode,
        discount,
        expiresAt,
        usageLimit,
      });
      return res.data?.data as { token?: string };
    },
    onSuccess: (created) => {
      const token = created?.token;
      if (token) {
        navigator.clipboard.writeText(payUrl(token)).catch(() => {});
        toast.success('Link created — URL copied to clipboard');
      } else {
        toast.success('Link created');
      }
      queryClient.invalidateQueries({ queryKey: qk.paymentLinks() });
      onCreated();
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not create link')),
  });

  function submit() {
    if (!teamId || selected.length === 0 || !title.trim()) {
      toast.error('Pick a team, at least one product, and a title');
      return;
    }
    createMutation.mutate();
  }

  const disabledSubmit = useMemo(
    () =>
      createMutation.isPending ||
      !teamId ||
      selected.length === 0 ||
      !title.trim() ||
      Boolean(previewError),
    [createMutation.isPending, teamId, selected, title, previewError]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">New payment link</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        <div className="grid gap-4">
          <label className="block text-sm">
            Customer account
            <select
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
            >
              <option value="">Select a team…</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.plan})
                </option>
              ))}
            </select>
          </label>

          {teamId && !hasRegion && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
              <p className="mb-2 text-sm font-medium text-amber-800">
                This account has no country/currency set. Currency (and the payment gateway + tax)
                is derived from the country — set it to continue.
              </p>
              <div className="flex items-end gap-2">
                <label className="block text-sm">
                  Country
                  <select
                    value={regionCountry}
                    onChange={(e) => setRegionCountry(e.target.value)}
                    className="mt-1 w-48 rounded-lg border bg-white px-3 py-2"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name} ({c.currency})
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  onClick={() => regionMutation.mutate()}
                  disabled={regionMutation.isPending}
                  className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {regionMutation.isPending ? 'Saving…' : 'Set region'}
                </button>
              </div>
            </div>
          )}

          {teamId && hasRegion && (
            <div className="rounded-lg border p-3">
              <p className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-400">
                <span>
                  Products {currency && `· priced in ${currency}`}
                  {country && ` · ${country}`}
                </span>
                <button
                  type="button"
                  onClick={() => setHasRegion(false)}
                  className="text-[11px] normal-case text-blue-600 hover:underline"
                >
                  change country/currency
                </button>
              </p>
              {products.length === 0 ? (
                <p className="text-sm text-gray-400">Loading products…</p>
              ) : (
                <div className="space-y-2">
                  {products.map((p) => (
                    <div key={key(p)} className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-700">{p.name}</span>
                      <select
                        value={selectedTierFor(p)}
                        onChange={(e) => setTier(p, e.target.value)}
                        className="w-56 rounded-lg border bg-white px-2 py-1.5 text-sm"
                      >
                        <option value="">— not included —</option>
                        {p.tiers.map((t) => (
                          <option key={t.tier} value={t.tier}>
                            {/* ProductTier.price is canonical minor units */}
                            {t.displayName} ({formatMoney(t.price, t.currency)})
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <label className="block text-sm">
            Title
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Tyashin Pro — Acme Corp"
              className="mt-1 w-full rounded-lg border px-3 py-2"
            />
          </label>

          <label className="block text-sm">
            Description (optional)
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-lg border px-3 py-2"
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block text-sm">
              Frequency
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
              >
                {FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Tax
              <select
                value={taxMode}
                onChange={(e) => setTaxMode(e.target.value as any)}
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
              >
                <option value="auto">Auto (by gateway)</option>
                <option value="inclusive">Tax-inclusive</option>
                <option value="exclusive">Tax-exclusive</option>
              </select>
            </label>
            <label className="block text-sm">
              Expires in (days)
              <input
                type="number"
                min={1}
                value={expiryDays}
                onChange={(e) => setExpiryDays(Number(e.target.value))}
                className="mt-1 w-full rounded-lg border px-3 py-2"
              />
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block text-sm">
              Discount (before tax)
              <select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value as any)}
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
              >
                <option value="none">No discount</option>
                <option value="percent">Percentage off</option>
                <option value="amount">Amount off per month</option>
              </select>
            </label>
            {discountType !== 'none' && (
              <label className="block text-sm">
                {discountType === 'percent'
                  ? 'Percent off'
                  : `Amount off / month${currency ? ` (${currency})` : ''}`}
                <input
                  type="number"
                  min={0}
                  max={discountType === 'percent' ? 99.99 : undefined}
                  step="0.01"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountType === 'percent' ? 'e.g. 10' : 'e.g. 18'}
                  className="mt-1 w-full rounded-lg border px-3 py-2"
                />
              </label>
            )}
          </div>
          {discountType !== 'none' && (
            <p className="-mt-2 text-xs text-gray-500">
              Applies to every billing cycle. The customer sees only the net discount as a
              percentage.
            </p>
          )}

          <label className="block text-sm">
            Usage limit
            <input
              type="number"
              min={1}
              value={usageLimit}
              onChange={(e) => setUsageLimit(Number(e.target.value))}
              className="mt-1 w-40 rounded-lg border px-3 py-2"
            />
          </label>

          {previewError && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {previewError}
            </p>
          )}

          {preview && (
            <div className="rounded-lg border bg-blue-50 p-4 text-sm">
              {preview.discountAmount > 0 && (
                <div className="mb-2 flex items-baseline justify-between text-gray-600">
                  <span>
                    List price <span className="line-through">{preview.formattedListAmount}</span>
                  </span>
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                    {Number(preview.discountPercent.toFixed(2))}% off · −
                    {fmtMinor(preview.discountAmount, preview.currency)}
                  </span>
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <span className="font-medium text-gray-700">Customer pays</span>
                <span className="text-xl font-bold text-gray-900">
                  {preview.formattedDisplayAmount} / {frequency}
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Sold by {preview.tax.entityLegalName} ·{' '}
                {preview.tax.gatewayComputed
                  ? `${preview.tax.taxName} added on top at checkout (Stripe Tax)`
                  : preview.tax.pricing === 'inclusive'
                    ? `incl. ${preview.tax.taxRate ?? 0}% ${preview.tax.taxName} (${fmtMinor(preview.tax.taxAmount, preview.currency)})`
                    : `plus ${preview.tax.taxRate ?? 0}% ${preview.tax.taxName} (+${fmtMinor(preview.tax.taxAmount, preview.currency)})`}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm">
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={disabledSubmit}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating…' : 'Create link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
